/* RevisionInlineBanner — Section-level rose banner that surfaces the
 * doctor's revision requests inline above each affected plan-editor
 * section.
 *
 * iter98.89 — Replaces the verbose "tick every row" panel on the
 * Doctor's Submission tab with a tactical, in-context decision UI.
 * Each row carries two per-row CTAs:
 *
 *   1. "Accept & apply" — for struct rows, sets the editor field to
 *      the doctor's requested value (and stamps a green badge). For
 *      free-text rows, marks the row addressed without modifying any
 *      field (since there's no automatable change for narrative items).
 *
 *   2. "Deny — add rationale" — expands a small textarea below the
 *      row. The planner explains why the request can't be honoured.
 *      The rationale flows back to the doctor on resubmit (visible in
 *      the ReviewApprovalPage diff column).
 *
 * Decisions are LIFTED to the parent (PlannerCaseDetail) — this banner
 * is presentational + dispatches `onDecide` events upward. That keeps
 * the resubmit footer (also in PlannerCaseDetail) in sync without
 * cross-component messaging.
 */
import React, { useState } from "react";
import {
    ArrowUUpLeft, CheckCircle, ProhibitInset, PencilSimple, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SEVERITY_PILL = {
    critical: "bg-rose-100 text-rose-800 ring-1 ring-rose-300",
    normal:   "bg-amber-100 text-amber-900 ring-1 ring-amber-300",
    minor:    "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300",
};

/* Compact "from → to" pill for struct rows. Handles numeric + string
 * values; falls back to "—" when the current plan doesn't yet have a
 * value for the field. */
function StructDiff({ row }) {
    const fmt = (v) => {
        if (v === null || v === undefined || v === "") return "—";
        return `${v}${row.unit ? ` ${row.unit}` : ""}`;
    };
    return (
        <span className="font-mono text-[12px] text-rose-900/90">
            <span className="text-rose-900/60 line-through decoration-rose-300/80">{fmt(row.from)}</span>
            <span className="mx-1.5 text-rose-700">→</span>
            <span className="font-bold text-rose-900">{fmt(row.to)}</span>
        </span>
    );
}

/* Single row inside the banner. Self-manages its denial textarea state
 * so the parent only tracks decisions (not in-flight UI like "denial
 * draft text"). */
function RevisionRow({ row, decision, onDecide, onClear, readOnly }) {
    const [denying, setDenying] = useState(false);
    const [rationale, setRationale] = useState("");

    const accepted = decision?.kind === "accepted";
    const denied = decision?.kind === "denied";
    const decided = accepted || denied;

    const accept = () => {
        // For struct rows, snap the editor field to the requested value.
        // Free rows simply get acknowledged.
        onDecide(row.key, {
            kind: "accepted",
            applied_value: row.kind === "struct" ? row.to : null,
        });
    };

    const openDenyComposer = () => {
        setDenying(true);
        setRationale(decision?.rationale || "");
    };

    const submitDenial = () => {
        const text = rationale.trim();
        if (!text) return;
        onDecide(row.key, { kind: "denied", rationale: text });
        setDenying(false);
    };

    const undecide = () => {
        onClear(row.key);
        setDenying(false);
        setRationale("");
    };

    return (
        <div
            className={`rounded-lg border px-3.5 py-2.5 transition-colors ${
                accepted ? "bg-emerald-50/70 border-emerald-200"
                : denied ? "bg-amber-50/70 border-amber-200"
                : "bg-white border-rose-200"
            }`}
            data-testid={`revision-inline-row-${row.key}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] font-semibold text-foreground">{row.label}</span>
                        {row.kind === "free" && row.severity && (
                            <span className={`text-[10px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${SEVERITY_PILL[row.severity] || SEVERITY_PILL.normal}`}>
                                {row.severity}
                            </span>
                        )}
                        {row.kind === "free" && row.teeth?.length > 0 && (
                            <span className="text-[10px] text-rose-700/80 font-mono">
                                {row.teeth.length <= 6 ? `teeth ${row.teeth.join(", ")}` : `${row.teeth.length} teeth`}
                            </span>
                        )}
                    </div>
                    <div className="mt-1">
                        {row.kind === "struct"
                            ? <StructDiff row={row} />
                            : (
                                <p className="text-[12.5px] text-rose-900/90 leading-relaxed whitespace-pre-wrap">
                                    {row.note || <span className="italic text-rose-700/60">No note provided.</span>}
                                </p>
                            )
                        }
                    </div>
                </div>

                {/* Per-row action cluster */}
                {!decided && !denying && !readOnly && (
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={accept}
                            className="h-7 px-2.5 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                            data-testid={`revision-row-accept-${row.key}`}
                        >
                            <CheckCircle size={12} weight="duotone" className="mr-1" />
                            {row.kind === "struct" ? "Accept & apply" : "Accept"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={openDenyComposer}
                            className="h-7 px-2.5 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50"
                            data-testid={`revision-row-deny-${row.key}`}
                        >
                            <ProhibitInset size={12} weight="duotone" className="mr-1" />
                            Deny
                        </Button>
                    </div>
                )}

                {decided && !readOnly && (
                    <button
                        onClick={undecide}
                        className="text-[10px] text-muted-foreground hover:text-foreground underline shrink-0 mt-1"
                        data-testid={`revision-row-clear-${row.key}`}
                    >
                        change
                    </button>
                )}
            </div>

            {/* Decision badge */}
            {accepted && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800">
                    <CheckCircle size={13} weight="fill" />
                    Accepted{row.kind === "struct" ? ` · applied to editor (${row.to}${row.unit ? ` ${row.unit}` : ""})` : ""}
                </div>
            )}
            {denied && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-100/40 px-2.5 py-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800 mb-0.5 flex items-center gap-1.5">
                        <ProhibitInset size={12} weight="fill" />
                        Denied
                    </div>
                    <p className="text-[12px] text-amber-950 leading-relaxed whitespace-pre-wrap">
                        {decision.rationale}
                    </p>
                </div>
            )}

            {/* Inline denial composer */}
            {denying && !decided && (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid={`revision-row-deny-composer-${row.key}`}>
                    <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800 mb-1.5 flex items-center gap-1.5">
                        <PencilSimple size={11} weight="duotone" /> Why can&apos;t this be honoured?
                    </label>
                    <Textarea
                        value={rationale}
                        onChange={(e) => setRationale(e.target.value)}
                        rows={2}
                        placeholder="e.g. Reducing IPR below 0.3mm on 13-14 would compromise the rotation. Suggest keeping the current value and revisiting at refinement."
                        className="text-[12px] bg-white border-amber-200"
                        data-testid={`revision-row-deny-textarea-${row.key}`}
                        autoFocus
                    />
                    <div className="flex items-center justify-end gap-2 mt-1.5">
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setDenying(false); setRationale(""); }}
                            className="h-7 px-2 text-[11px] text-amber-900"
                        >
                            <X size={11} className="mr-1" /> Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={submitDenial}
                            disabled={!rationale.trim()}
                            className="h-7 px-3 text-[11px] bg-amber-700 hover:bg-amber-800 text-white"
                            data-testid={`revision-row-deny-save-${row.key}`}
                        >
                            Save rationale
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* The banner itself. Renders a rose-bordered card above the section
 * accordion. Counts addressed/total so the planner sees progress
 * inline (no need to scroll to the sticky footer). */
export default function RevisionInlineBanner({
    items = [], decisions = {}, onDecide, onClear, readOnly = false,
}) {
    if (!items.length) return null;
    const total = items.length;
    const addressed = items.filter((r) => decisions[r.key]).length;
    const allDone = addressed >= total;

    return (
        <div
            className={`rounded-xl border-2 ${allDone ? "border-emerald-200 bg-emerald-50/30" : "border-rose-200 bg-rose-50/40"} mb-3 overflow-hidden`}
            data-testid="revision-inline-banner"
        >
            <div className={`px-4 py-2.5 flex items-center justify-between gap-3 ${allDone ? "bg-emerald-100/50" : "bg-rose-100/50"}`}>
                <div className="flex items-center gap-2 min-w-0">
                    <ArrowUUpLeft size={14} weight="fill" className={allDone ? "text-emerald-700" : "text-rose-700"} />
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] truncate">
                        <span className={allDone ? "text-emerald-800" : "text-rose-800"}>Doctor&apos;s revision</span>
                        <span className="ml-1.5 text-muted-foreground font-medium normal-case tracking-normal">
                            · {addressed} of {total} addressed
                        </span>
                    </div>
                </div>
                {allDone && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-800">
                        <CheckCircle size={12} weight="fill" /> Section done
                    </span>
                )}
            </div>
            <div className="px-3 py-3 space-y-2">
                {items.map((row) => (
                    <RevisionRow
                        key={row.key}
                        row={row}
                        decision={decisions[row.key]}
                        onDecide={onDecide}
                        onClear={onClear}
                        readOnly={readOnly}
                    />
                ))}
            </div>
        </div>
    );
}
